import { http } from "./http";

// ============================================
// Enums (aligned with backend)
// ============================================

export enum DealStatus {
    OPEN = "OPEN",
    WON = "WON",
    LOST = "LOST",
}

export enum DealStage {
    PROSPECTING = "prospecting",
    QUALIFICATION = "qualification",
    PROPOSAL = "proposal",
    NEGOTIATION = "negotiation",
    CLOSED_WON = "closed-won",
    CLOSED_LOST = "closed-lost",
}

export enum DealPriority {
    LOW = "low",
    MEDIUM = "medium",
    HIGH = "high",
    URGENT = "urgent",
}

export enum DealLostReason {
    PRICE = "price",
    COMPETITION = "competition",
    TIMING = "timing",
    NO_BUDGET = "no-budget",
    NO_DECISION = "no-decision",
    PRODUCT_FIT = "product-fit",
    OTHER = "other",
}

export enum ActivityType {
    CALL = "call",
    EMAIL = "email",
    MEETING = "meeting",
    TASK = "task",
    NOTE = "note",
    DEADLINE = "deadline",
}

export enum ActivityPriority {
    LOW = "low",
    MEDIUM = "medium",
    HIGH = "high",
}

export enum ActivityStatus {
    OPEN = "OPEN",
    DONE = "DONE",
}

export enum ActivityOutcome {
    COMPLETED = "completed",
    NO_ANSWER = "no-answer",
    LEFT_VOICEMAIL = "left-voicemail",
    RESCHEDULED = "rescheduled",
}

export enum Industry {
    TECHNOLOGY = "technology",
    FINANCE = "finance",
    HEALTHCARE = "healthcare",
    RETAIL = "retail",
    MANUFACTURING = "manufacturing",
    EDUCATION = "education",
    REAL_ESTATE = "real-estate",
}

export enum CompanySize {
    SIZE_1_10 = "1-10",
    SIZE_11_50 = "11-50",
    SIZE_51_200 = "51-200",
    SIZE_201_500 = "201-500",
    SIZE_501_1000 = "501-1000",
    SIZE_1000_PLUS = "1000+",
}

export enum CompanyStatus {
    PROSPECT = "prospect",
    ACTIVE = "active",
    CHURNED = "churned",
    INACTIVE = "inactive",
}

export enum ContactStatus {
    ACTIVE = "active",
    INACTIVE = "inactive",
    BOUNCED = "bounced",
    UNSUBSCRIBED = "unsubscribed",
}

export enum PreferredContactMethod {
    EMAIL = "email",
    PHONE = "phone",
    MOBILE = "mobile",
}

export enum LeadSource {
    WEBSITE = "website",
    REFERRAL = "referral",
    COLD_CALL = "cold-call",
    SOCIAL_MEDIA = "social-media",
    EVENT = "event",
    PARTNER = "partner",
    ADVERTISING = "advertising",
    OTHER = "other",
}

export enum UserStatus {
    ACTIVE = "ACTIVE",
    INVITED = "INVITED",
    DISABLED = "DISABLED",
}

export enum WorkspaceRole {
    OWNER = "OWNER",
    ADMIN = "ADMIN",
    MEMBER = "MEMBER",
}

// ============================================
// Shared Sub-types
// ============================================

export type UserSummary = {
    id: string;
    fullName: string;
};

// ============================================
// Backend Entity Types
// ============================================

export type Company = {
    id: string;
    name: string;
    website: string;
    industry: Industry;
    companySize?: CompanySize;
    status: CompanyStatus;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    leadSource: LeadSource;
    numberOfEmployees?: number;
    annualRevenue?: number;
    linkedinUrl?: string;
    timezone?: string;
    description?: string;
    lastActivityAt?: string;
    workspaceId: string;
    ownerId?: string;
    owner?: UserSummary;
    createdBy?: string;
    createdByUser?: UserSummary;
    updatedBy?: string;
    updatedByUser?: UserSummary;
    createdAt: string;
    updatedAt: string;
    deletedAt?: string;
    deletedBy?: string;
};

export type Contact = {
    id: string;
    name: string;
    email: string;
    phone?: string;
    mobile?: string;
    jobTitle?: string;
    department?: string;
    linkedinUrl?: string;
    status: ContactStatus;
    leadSource?: LeadSource;
    preferredContactMethod?: PreferredContactMethod;
    isPrimary: boolean;
    doNotContact: boolean;
    companyId: string;
    company?: Company;
    assignedTo: string;
    assignedUser?: UserSummary;
    ownerId?: string;
    owner?: UserSummary;
    lastActivityAt?: string;
    workspaceId: string;
    createdBy?: string;
    createdByUser?: UserSummary;
    updatedBy?: string;
    updatedByUser?: UserSummary;
    createdAt: string;
    updatedAt: string;
    deletedAt?: string;
};

export type Deal = {
    id: string;
    title: string;
    stage: DealStage;
    dealValue: number;
    currency: string;
    status: DealStatus;
    priority: DealPriority;
    probability?: number;
    expectedCloseDate?: string;
    actualCloseDate?: string;
    lostReason?: DealLostReason;
    source?: LeadSource;
    description?: string;
    companyId: string;
    contactId?: string;
    assignedTo: string;
    ownerId?: string;
    company?: Company;
    contact?: Contact;
    owner?: UserSummary;
    assignedUser?: UserSummary;
    createdBy?: string;
    createdByUser?: UserSummary;
    updatedBy?: string;
    updatedByUser?: UserSummary;
    workspaceId: string;
    createdAt: string;
    updatedAt: string;
    deletedAt?: string;
};

export type Activity = {
    id: string;
    type: ActivityType;
    subject: string;
    body?: string;
    status: ActivityStatus;
    priority: ActivityPriority;
    outcome?: ActivityOutcome;
    dueDate: string;
    dueTime: string;
    reminderAt?: string;
    location?: string;
    duration?: number;
    contactId: string;
    dealId?: string;
    companyId?: string;
    assignedTo: string;
    ownerId?: string;
    contact?: Contact;
    deal?: Deal;
    company?: Company;
    owner?: UserSummary;
    assignedUser?: UserSummary;
    createdBy?: string;
    createdByUser?: UserSummary;
    updatedBy?: string;
    updatedByUser?: UserSummary;
    workspaceId: string;
    createdAt: string;
    updatedAt: string;
    deletedAt?: string;
};

export type User = {
    id: string;
    fullName: string;
    email?: string;
    avatarUrl?: string;
    status: UserStatus;
    externalAuthId?: string;
    externalAuthProvider?: string;
    createdAt: string;
    updatedAt: string;
    workspaces?: WorkspaceMember[];
};

export type WorkspaceMember = {
    workspaceId: string;
    userId: string;
    role: WorkspaceRole;
};

export type ThemeSettings = {
    primaryColor: string;
    secondaryColor: string;
    textPrimaryColor: string;
    textSecondaryColor: string;
    backgroundPrimaryColor: string;
};

export type BootstrapConfig = {
    _id: string;
    workspaceId: string;
    userId: string;
    themeSettings?: ThemeSettings;
    dashboardItems?: unknown[];
    createdAt: string;
    updatedAt: string;
};

// ============================================
// API Request Types
// ============================================

export type CreateCompanyInput = {
    name: string;
    website: string;
    industry: Industry;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    leadSource: LeadSource;
    companySize?: CompanySize;
    status?: CompanyStatus;
    numberOfEmployees?: number;
    annualRevenue?: number;
    linkedinUrl?: string;
    timezone?: string;
    description?: string;
    ownerId?: string;
};

export type UpdateCompanyInput = Partial<CreateCompanyInput>;

export type CreateContactInput = {
    name: string;
    email: string;
    companyId: string;
    assignedTo: string;
    phone?: string;
    mobile?: string;
    jobTitle?: string;
    department?: string;
    linkedinUrl?: string;
    status?: ContactStatus;
    leadSource?: LeadSource;
    preferredContactMethod?: PreferredContactMethod;
    isPrimary?: boolean;
    doNotContact?: boolean;
};

export type UpdateContactInput = Partial<CreateContactInput>;

export type CreateDealInput = {
    title: string;
    dealValue: number;
    stage: DealStage;
    priority: DealPriority;
    companyId: string;
    assignedTo: string;
    contactId?: string;
    currency?: string;
    status?: DealStatus;
    probability?: number;
    expectedCloseDate?: string;
    actualCloseDate?: string;
    lostReason?: DealLostReason;
    source?: LeadSource;
    description?: string;
};

export type UpdateDealInput = Partial<CreateDealInput> & {
    contactId?: string | null;
};

export type CreateActivityInput = {
    type: ActivityType;
    priority: ActivityPriority;
    subject: string;
    dueDate: string;
    dueTime: string;
    contactId: string;
    assignedTo: string;
    body?: string;
    status?: ActivityStatus;
    outcome?: ActivityOutcome;
    reminderAt?: string;
    location?: string;
    duration?: number;
    dealId?: string;
    companyId?: string;
};

export type UpdateActivityInput = Partial<CreateActivityInput> & {
    dealId?: string | null;
    companyId?: string | null;
};

export type CreateUserInput = {
    fullName: string;
    email?: string;
    avatarUrl?: string;
    externalAuthId?: string;
    externalAuthProvider?: string;
};

export type UpdateUserInput = Partial<CreateUserInput>;

// ============================================
// API Response Types
// ============================================

export type PaginatedResponse<T> = {
    data: T[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
};

export type ApiErrorResponse = {
    statusCode: number;
    message: string;
    errors?: Array<{
        field?: string;
        path?: string[];
        message: string;
    }>;
};

export type ListQueryParams = {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    stage?: string;
    priority?: string;
    status?: string;
    companyId?: string;
    contactId?: string;
    assignedTo?: string;
    ownerId?: string;
    type?: string;
    industry?: string;
};

// Report Response Types
export type DealByStageReport = {
    stage: string;
    count: string;
    totalValue: string;
};

export type DealByMonthReport = {
    month: string;
    count: string;
    totalValue: string;
};

export type ActivityByTypeReport = {
    type: string;
    count: string;
};

export type KpiSummaryReport = {
    totalPipeline: number;
    activeContacts: number;
    dealsWonThisMonth: { count: number; value: number };
    activitiesThisWeek: number;
};

export type PipelineFunnelReport = {
    stage: string;
    count: number;
    totalValue: number;
    conversionRate: number | null;
};

export type RevenueForecastReport = {
    month: string;
    expectedRevenue: number;
    dealCount: number;
};

export type WinLossReport = {
    summary: { won: number; lost: number; winRate: number };
    monthly: Array<{
        month: string;
        won: number;
        lost: number;
        wonValue: number;
        lostValue: number;
    }>;
    lostReasons: Array<{ reason: string; count: number }>;
};

export type ValueDistributionReport = {
    bucket: string;
    count: number;
    totalValue: number;
};

export type TopDealReport = {
    id: string;
    title: string;
    dealValue: number;
    status: DealStatus;
    stage: DealStage;
    companyName: string;
    assignedToName: string;
    expectedCloseDate: string | null;
};

export type ActivityByUserReport = {
    userName: string;
    activities: Partial<Record<ActivityType, number>>;
    total: number;
};

export type ContactGrowthReport = {
    month: string;
    newContacts: number;
    cumulative: number;
};

export type CompanyByIndustryReport = {
    industry: string;
    count: number;
    percentage: number;
};

// ============================================
// Helper Functions
// ============================================

const buildQueryString = (params: ListQueryParams): string => {
    const searchParams = new URLSearchParams();
    (Object.entries(params) as [string, string | number | undefined][]).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            // API expects sortOrder as uppercase ASC/DESC
            const serialized = key === "sortOrder" ? String(value).toUpperCase() : String(value);
            searchParams.set(key, serialized);
        }
    });
    const query = searchParams.toString();
    return query ? `?${query}` : "";
};

// ---- Route base ----
const V1 = "/api/v1";

export const crmApi = {
    // Companies
    listCompanies: (params: ListQueryParams = {}) =>
        http<PaginatedResponse<Company>>(`${V1}/companies${buildQueryString(params)}`),
    getCompany: (id: string) => http<Company>(`${V1}/companies/${id}`),
    createCompany: (payload: CreateCompanyInput) =>
        http<Company>(`${V1}/companies`, { method: "POST", body: JSON.stringify(payload) }),
    updateCompany: (id: string, payload: UpdateCompanyInput) =>
        http<Company>(`${V1}/companies/${id}`, {
            method: "PATCH",
            body: JSON.stringify(payload),
        }),
    deleteCompany: (id: string) => http<void>(`${V1}/companies/${id}`, { method: "DELETE" }),
    restoreCompany: (id: string) =>
        http<Company>(`${V1}/companies/${id}/restore`, { method: "PATCH" }),
    bulkDeleteCompanies: (ids: string[]) =>
        http<{ deleted: number }>(`${V1}/companies/bulk-delete`, {
            method: "POST",
            body: JSON.stringify({ ids }),
        }),
    listCompanyContacts: (companyId: string, params: ListQueryParams = {}) =>
        http<PaginatedResponse<Contact>>(
            `${V1}/companies/${companyId}/contacts${buildQueryString(params)}`,
        ),
    listCompanyDeals: (companyId: string, params: ListQueryParams = {}) =>
        http<PaginatedResponse<Deal>>(
            `${V1}/companies/${companyId}/deals${buildQueryString(params)}`,
        ),

    // Contacts
    listContacts: (params: ListQueryParams = {}) =>
        http<PaginatedResponse<Contact>>(`${V1}/contacts${buildQueryString(params)}`),
    getContact: (id: string) => http<Contact>(`${V1}/contacts/${id}`),
    createContact: (payload: CreateContactInput) =>
        http<Contact>(`${V1}/contacts`, { method: "POST", body: JSON.stringify(payload) }),
    updateContact: (id: string, payload: UpdateContactInput) =>
        http<Contact>(`${V1}/contacts/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
    deleteContact: (id: string) => http<void>(`${V1}/contacts/${id}`, { method: "DELETE" }),
    restoreContact: (id: string) =>
        http<Contact>(`${V1}/contacts/${id}/restore`, { method: "PATCH" }),
    bulkDeleteContacts: (ids: string[]) =>
        http<{ deleted: number }>(`${V1}/contacts/bulk-delete`, {
            method: "POST",
            body: JSON.stringify({ ids }),
        }),
    listContactDeals: (contactId: string, params: ListQueryParams = {}) =>
        http<PaginatedResponse<Deal>>(
            `${V1}/contacts/${contactId}/deals${buildQueryString(params)}`,
        ),
    listContactActivities: (contactId: string, params: ListQueryParams = {}) =>
        http<PaginatedResponse<Activity>>(
            `${V1}/contacts/${contactId}/activities${buildQueryString(params)}`,
        ),

    // Deals
    listDeals: (params: ListQueryParams = {}) =>
        http<PaginatedResponse<Deal>>(`${V1}/deals${buildQueryString(params)}`),
    getDeal: (id: string) => http<Deal>(`${V1}/deals/${id}`),
    createDeal: (payload: CreateDealInput) =>
        http<Deal>(`${V1}/deals`, { method: "POST", body: JSON.stringify(payload) }),
    updateDeal: (id: string, payload: UpdateDealInput) =>
        http<Deal>(`${V1}/deals/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
    deleteDeal: (id: string) => http<void>(`${V1}/deals/${id}`, { method: "DELETE" }),
    restoreDeal: (id: string) => http<Deal>(`${V1}/deals/${id}/restore`, { method: "PATCH" }),
    bulkDeleteDeals: (ids: string[]) =>
        http<{ deleted: number }>(`${V1}/deals/bulk-delete`, {
            method: "POST",
            body: JSON.stringify({ ids }),
        }),
    listDealActivities: (dealId: string, params: ListQueryParams = {}) =>
        http<PaginatedResponse<Activity>>(
            `${V1}/deals/${dealId}/activities${buildQueryString(params)}`,
        ),

    // Activities
    listActivities: (params: ListQueryParams = {}) =>
        http<PaginatedResponse<Activity>>(`${V1}/activities${buildQueryString(params)}`),
    getActivity: (id: string) => http<Activity>(`${V1}/activities/${id}`),
    createActivity: (payload: CreateActivityInput) =>
        http<Activity>(`${V1}/activities`, { method: "POST", body: JSON.stringify(payload) }),
    updateActivity: (id: string, payload: UpdateActivityInput) =>
        http<Activity>(`${V1}/activities/${id}`, {
            method: "PATCH",
            body: JSON.stringify(payload),
        }),
    deleteActivity: (id: string) => http<void>(`${V1}/activities/${id}`, { method: "DELETE" }),
    restoreActivity: (id: string) =>
        http<Activity>(`${V1}/activities/${id}/restore`, { method: "PATCH" }),
    bulkDeleteActivities: (ids: string[]) =>
        http<{ deleted: number }>(`${V1}/activities/bulk-delete`, {
            method: "POST",
            body: JSON.stringify({ ids }),
        }),

    // Reports
    getDealReportsByStage: () =>
        http<{ data: DealByStageReport[] }>(`${V1}/deals/reports/by-stage`),
    getDealReportsByMonth: () =>
        http<{ data: DealByMonthReport[] }>(`${V1}/deals/reports/by-month`),
    getActivityReportsByType: () =>
        http<{ data: ActivityByTypeReport[] }>(`${V1}/activities/reports/by-type`),
    getKpiSummary: () => http<KpiSummaryReport>(`${V1}/reports/kpi-summary`),
    getPipelineFunnel: () =>
        http<{ data: PipelineFunnelReport[] }>(`${V1}/deals/reports/pipeline-funnel`),
    getRevenueForecast: (months = 6) =>
        http<{ data: RevenueForecastReport[] }>(
            `${V1}/deals/reports/revenue-forecast?months=${months}`,
        ),
    getWinLoss: (period = "12m") =>
        http<WinLossReport>(`${V1}/deals/reports/win-loss?period=${period}`),
    getValueDistribution: () =>
        http<{ data: ValueDistributionReport[] }>(`${V1}/deals/reports/value-distribution`),
    getTopDeals: (limit = 10) =>
        http<{ data: TopDealReport[] }>(`${V1}/deals/reports/top-deals?limit=${limit}`),
    getActivitiesByUser: () =>
        http<{ data: ActivityByUserReport[] }>(`${V1}/activities/reports/by-user`),
    getContactGrowth: () => http<{ data: ContactGrowthReport[] }>(`${V1}/contacts/reports/growth`),
    getCompaniesByIndustry: () =>
        http<{ data: CompanyByIndustryReport[] }>(`${V1}/companies/reports/by-industry`),

    // Users
    listUsers: (params: ListQueryParams = {}) =>
        http<PaginatedResponse<User>>(`${V1}/users${buildQueryString(params)}`),
    getUser: (id: string) => http<User>(`${V1}/users/${id}`),
    createUser: (payload: CreateUserInput) =>
        http<User>(`${V1}/users`, { method: "POST", body: JSON.stringify(payload) }),
    updateUser: (id: string, payload: UpdateUserInput) =>
        http<User>(`${V1}/users/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
    deleteUser: (id: string) => http<void>(`${V1}/users/${id}`, { method: "DELETE" }),
    restoreUser: (id: string) => http<User>(`${V1}/users/${id}/restore`, { method: "PATCH" }),
    updateUserRole: (id: string, role: WorkspaceRole) =>
        http<User>(`${V1}/users/${id}/role`, { method: "PATCH", body: JSON.stringify({ role }) }),

    // Bootstrap (Dashboard Config)
    getBootstrap: () => http<BootstrapConfig>(`${V1}/bootstrap`),
    updateBootstrap: (payload: {
        themeSettings?: Partial<ThemeSettings>;
        dashboardItems?: unknown[];
    }) =>
        http<BootstrapConfig>(`${V1}/bootstrap`, {
            method: "PUT",
            body: JSON.stringify(payload),
        }),

    // Health Check
    healthCheck: () => http<{ status: string; timestamp: string }>("/health"),
};
